// src/enroll/EnrollWithPopup.jsx
import React, { useState, useEffect, useRef } from 'react';
import styles from './EnrollWithPopup.module.css';
import Captcha from '../components/includes/Captcha';
import { API } from "../config";

const EnrollWithPopup = () => {
    const [courses, setCourses] = useState([]);
    const [coursesLoading, setCoursesLoading] = useState(true);
    const [coursesError, setCoursesError] = useState(false);
    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', courseId: '', message: '',
    });
    const [captchaInput, setCaptchaInput] = useState('');
    const [captchaValue, setCaptchaValue] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const captchaRef = useRef(null);

    const fetchCourses = async () => {
        setCoursesLoading(true);
        setCoursesError(false);
        try {
            const response = await fetch(API.COURSES);
            if (!response.ok) throw new Error('Failed to fetch courses');
            const data = await response.json();
            setCourses(data);
        } catch (error) {
            console.error('Error fetching courses:', error);
            setCoursesError(true);
        } finally {
            setCoursesLoading(false);
        }
    };

    useEffect(() => {
        fetchCourses();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'name' && /\d/.test(value)) return;
        if (name === 'phone') {
            if (!/^\d*$/.test(value) || value.length > 10) return;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({ name: '', email: '', phone: '', courseId: '', message: '' });
        setCaptchaInput('');
        setCaptchaValue('');
        if (captchaRef.current?.refreshCaptcha) {
            captchaRef.current.refreshCaptcha();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert('Please enter a valid email address.');
            return;
        }
        if (formData.phone.length !== 10) {
            alert('Phone number must be exactly 10 digits long.');
            return;
        }
        if (captchaInput.toLowerCase() !== captchaValue.toLowerCase()) {
            alert('Incorrect CAPTCHA. Please try again.');
            return;
        }
        try {
            const response = await fetch(API.ENROLL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || 'Something went wrong');
            setSubmitted(true);
            resetForm();
        } catch (error) {
            console.error('Failed to submit form:', error);
            alert('Error submitting form: ' + error.message);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.bgImage} />
            <div className={styles.overlay} />

            <div className={styles.card}>
                <div className={styles.logoWrap}>
                    <img
                        src="/assets/imgs/Logo_light.png"
                        alt="BSpark Technologies"
                        className={styles.logo}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>

                <h3 className={styles.title}>Quick Enquiry</h3>
                <p className={styles.subtitle}>Fill in your details and we'll get back to you shortly.</p>

                {submitted ? (
                    <div className={styles.successBox}>
                        <div className={styles.successIcon}>✓</div>
                        <h4>Thank you!</h4>
                        <p>Our team will contact you soon.</p>
                        <button className={styles.submitBtn} onClick={() => setSubmitted(false)}>
                            Submit Another
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className={styles.form}>

                        <div className={styles.inputGroup}>
                            <span className={styles.inputIcon}>👤</span>
                            <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your Name" required />
                        </div>

                        <div className={styles.inputGroup}>
                            <span className={styles.inputIcon}>✉️</span>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Your Email" required />
                        </div>

                        <div className={styles.inputGroup}>
                            <span className={styles.inputIcon}>📞</span>
                            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="Your Phone Number" maxLength="10" required />
                        </div>

                        {/* ── Course dropdown with loading / error states ── */}
                        <div className={styles.inputGroup}>
                            <span className={styles.inputIcon}>📚</span>
                            <select
                                name="courseId"
                                value={formData.courseId}
                                onChange={handleChange}
                                required
                                disabled={coursesLoading || coursesError}
                                className={coursesError ? styles.selectError : ''}
                            >
                                {coursesLoading && (
                                    <option value="" disabled>Loading courses...</option>
                                )}
                                {coursesError && (
                                    <option value="" disabled>Failed to load courses</option>
                                )}
                                {!coursesLoading && !coursesError && (
                                    <option value="" disabled>Select a Course</option>
                                )}
                                {courses.map((course) => (
                                    <option key={course.id} value={course.id}>
                                        {course.coursename}
                                    </option>
                                ))}
                            </select>
                            {/* Retry button if courses failed to load */}
                            {coursesError && (
                                <button
                                    type="button"
                                    className={styles.retryBtn}
                                    onClick={fetchCourses}
                                    title="Retry loading courses"
                                >
                                    ↺
                                </button>
                            )}
                        </div>

                        {/* Show API URL in console if courses are empty after load — helps debug */}
                        {!coursesLoading && !coursesError && courses.length === 0 && (
                            <p className={styles.noCourses}>
                                No courses available right now.
                            </p>
                        )}

                        <div className={styles.captchaRow}>
                            <Captcha setCaptchaValue={setCaptchaValue} ref={captchaRef} />
                            <input
                                type="text"
                                name="captcha"
                                value={captchaInput}
                                onChange={(e) => setCaptchaInput(e.target.value)}
                                placeholder="Enter CAPTCHA"
                                required
                                className={styles.captchaInput}
                            />
                        </div>

                        <button type="submit" className={styles.submitBtn}>
                            Submit Enquiry →
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default EnrollWithPopup;